import { authFetchInit } from "../utils/csrf";
import { useState, useEffect, useCallback } from "react";
import { COMPANY_URL, LOCAL_URL } from "../constants/apiUrls";
import { useNavigate } from "react-router-dom";

/* `page` / `pageSize` drive server-side pagination on the company search. */
function useCombinedCompanyData(
  searchTerm,
  code,
  trevexToken,
  worldBoxToken,
  noPopup = false,
  isPrompt = false,
  page,
  pageSize
) {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({
    totalPages: 1,
    currentPage: 1,
    totalResults: 0,
    serverPaged: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState("");
  const [fetchToggle, setFetchToggle] = useState(false); // Used to trigger refetch

  const refetch = useCallback(() => {
    setFetchToggle((prev) => !prev);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!searchTerm || searchTerm.trim().length < 3) {
        setError("Search term must have at least 3 characters");
        setData([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setMessage("");

      try {
        // Only appended when supplied, so existing callers keep their exact URL.
        const paging =
          (page ? `&page=${page}` : "") + (pageSize ? `&page_size=${pageSize}` : "");
        const localServerUrl = isPrompt
          ? `/api/api/gpt_prompts/search/?search=${searchTerm}&page=${page}`
          : `/api/company/search_new/?search=${searchTerm}&country_code=${code}${paging}`;
        
        // A GET, so no CSRF header is required — but the cookies still have
        // to be attached, and a raw fetch does not do that by default.
        const response = await fetch(localServerUrl, authFetchInit())
        
        const result = await response.json();
        
        if (response.ok) {
          // TIP-509 (bundled into TIP-493) — render whatever results came back.
          // The backend returns 200 with valid matches in `data` even when an
          // upstream enrichment source errors (error:true) or is capped
          // (warning:true), so those flags must not suppress results. We never
          // reveal where results come from: when results exist they simply show
          // (no message); warning keeps its existing "too many results" notice;
          // the existing "Error occurred" message is kept only when there are NO
          const rows = isPrompt ? result : result.data || [];
          setData(rows);
          /* Pagination envelope from SearchNewAPI. */
          const serverPaged = typeof result?.total_pages === "number";
          setMeta({
            totalPages: serverPaged ? result.total_pages : 1,
            currentPage: result?.current_page ?? 1,
            totalResults: result?.total_results ?? (Array.isArray(rows) ? rows.length : 0),
            serverPaged,
          });
          if (result.warning) {
            setMessage(
              "Too many results found. Please refine your search for better results."
            );
          } else if (result.error && (isPrompt ? !rows : rows.length === 0)) {
            setMessage("Error occurred while fetching data.");
          }
        } else {
          setError(result.detail || result.error || "An error occurred while fetching data.");
        }
      } catch (fetchError) {
        setError("Failed to fetch data. Please try again later.");
        console.error("Fetch Error:", fetchError);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [searchTerm, code, isPrompt, page, pageSize, trevexToken, fetchToggle]);

  return { data, isLoading, error, message, refetch, meta };
}

export default useCombinedCompanyData;

